import { MatDialog } from '@angular/material/dialog';
import { MAT_MENU_PANEL, MatMenu, MatMenuPanel } from '@angular/material/menu';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  SimpleChanges,
  OnChanges,
  Optional,
  Host,
  Inject,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';
import { RoleBuilderComponent } from '../role-builder/role-builder.component';
import { StoredItemsBrowserComponent } from '../components/stored-items-browser/stored-items-browser.component';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Log } from 'src/app/logger.service';

@Component({
    selector: 'app-role-dropdown',
    templateUrl: './role-dropdown.component.html',
    styleUrls: ['./role-dropdown.component.scss'],
    standalone: false
})
export class RoleDropdownComponent implements OnInit, OnChanges, OnDestroy {
  roles: RoleDefinition[] = [];
  recentlyUsedRoles: RoleDefinition[] = [];
  popularRoles: RoleDefinition[] = [];
  popularRolesExtended: RoleDefinition[] = [];
  popularRolesFirstColumn: RoleDefinition[] = [];
  popularRolesSecondColumn: RoleDefinition[] = [];
  private readonly RECENT_ROLES_KEY = 'recentlyUsedRoles';
  private readonly MAX_RECENT_ROLES = 10;
  private rolesSubscription?: Subscription;
  private rolesLoaded = false;

  @Input() preSelectedRole: RoleDefinition | null = null;
  @Input() allowNewRole = true;
  @Input() useChipDisplay: boolean = false;
  @Input() dropdownOnly: boolean = false;
  @Output() roleSelected = new EventEmitter<RoleDefinition>();

  @ViewChild('chipMenu', { static: false }) chipMenu?: MatMenu;

  selectedRoleId: string | null = null;

  constructor(
    private roleDefinitionService: RoleDefinitionsService,
    private dialog: MatDialog,
    @Optional() @Host() private matMenu?: MatMenu,
    @Optional() @Host() @Inject(MAT_MENU_PANEL) private matMenuPanel?: MatMenuPanel<any>
  ) {}

  ngOnInit() {}

  onMenuOpened() {
    // Load roles when menu is first opened
    if (!this.rolesLoaded && !this.rolesSubscription) {
      this.rolesSubscription = this.roleDefinitionService.roleDefinitions$
        .pipe(filter((roles) => roles !== null))
        .subscribe((roles) => {
          if (roles) {
            this.roles = roles.sort((a, b) => a.name.localeCompare(b.name));
            this.rolesLoaded = true;

            // Get popular roles (public scope with "Popular" category)
            const allPopularRoles = roles.filter(
              (role) => role.scope === 'public' && role.category === 'Popular'
            );

            this.popularRoles = allPopularRoles.slice(0, 4);
            this.popularRolesExtended = allPopularRoles.slice(0, 8);

            // Split extended popular roles into two columns
            const midPoint = Math.ceil(this.popularRolesExtended.length / 2);
            this.popularRolesFirstColumn = this.popularRolesExtended.slice(0, midPoint);
            this.popularRolesSecondColumn = this.popularRolesExtended.slice(midPoint);

            // Get recently used roles from localStorage
            this.loadRecentRoles();
          }
        });
    }
  }

  ngOnDestroy() {
    if (this.rolesSubscription) {
      this.rolesSubscription.unsubscribe();
    }
  }

  private loadRecentRoles() {
    const storedIds = this.getRecentRoleIds();
    if (storedIds.length > 0) {
      this.recentlyUsedRoles = storedIds
        .map((id) => this.roles.find((r) => r.id === id))
        .filter((role): role is RoleDefinition => role !== undefined)
        .slice(0, 4);
    } else {
      this.recentlyUsedRoles = [];
    }
  }

  private getRecentRoleIds(): string[] {
    try {
      const stored = localStorage.getItem(this.RECENT_ROLES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveRecentRole(roleId: string) {
    let recentIds = this.getRecentRoleIds();
    // Remove if already exists and add to front
    recentIds = recentIds.filter((id) => id !== roleId);
    recentIds.unshift(roleId);
    // Keep only the most recent MAX_RECENT_ROLES
    recentIds = recentIds.slice(0, this.MAX_RECENT_ROLES);

    try {
      localStorage.setItem(this.RECENT_ROLES_KEY, JSON.stringify(recentIds));
    } catch (e) {
      Log.s('Failed to save recent roles:', 'error', {});
    }

    // Reload recent roles to update the display
    this.loadRecentRoles();
  }

  onSelectionChange(roleId: string | null) {
    Log.d('RoleDropdownComponent', 'onSelectionChange', roleId);

    // Handle "New Role" selection
    if (roleId === 'new') {
      this.onNewRoleClick();
      return;
    }

    this.selectedRoleId = roleId;
    const role = this.roles.find((r) => r.id === roleId);

    Log.d('RoleDropdownComponent', 'role', role);
    // Save to recent roles if a role was selected
    if (roleId && role) {
      this.saveRecentRole(roleId);
    }

    this.roleSelected.emit(role);

    // Close the chip menu if it's open
    if (this.chipMenu) {
      this.chipMenu.closed.emit();
    }
  }

  clearSelection() {
    this.onSelectionChange(null);
  }

  get isFilterActive(): boolean {
    return !!this.selectedRoleId;
  }

  get selectedRole(): RoleDefinition | null {
    if (!this.selectedRoleId) return null;
    return this.roles.find((r) => r.id === this.selectedRoleId) || null;
  }

  get chipText(): string {
    const role = this.selectedRole;
    return role ? role.name : '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['preSelectedRole']) {
      // Only update selectedRoleId if it's actually different from what we already have
      const newRoleId = this.preSelectedRole?.id || null;

      if (this.selectedRoleId !== newRoleId) {
        // Add the preselected role to our list - we'll load the rest later
        if (this.preSelectedRole) {
          this.roles.push(this.preSelectedRole);
        }
        this.selectedRoleId = newRoleId;
      }
    }
  }

  trackByRoleId(index: number, role: RoleDefinition): string {
    return role.id;
  }

  onViewAll() {
    const dialogRef = this.dialog.open(StoredItemsBrowserComponent, {
      width: '95vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '1000px',
      panelClass: 'saved-lists-dialog',
      backdropClass: 'tw-bg-black/80',
      data: {
        items$: this.roleDefinitionService.roleDefinitions$,
        config: this.roleDefinitionService.getBrowserConfig(),
      },
    });

    dialogRef.afterClosed().subscribe((result: RoleDefinition | undefined) => {
      if (result) {
        this.onSelectionChange(result.id);
      }
    });
  }

  onNewRoleClick() {
    // Close the containing menu (if any) without changing selection
    this.matMenuPanel?.close?.emit();
    // Open the role builder dialog on the next tick
    setTimeout(() => {
      const dialogRef = this.dialog.open(RoleBuilderComponent, {
        maxWidth: '1200px',
        width: '95vw',
        panelClass: 'saved-lists-dialog',
        backdropClass: 'tw-bg-black/80',
      });

      dialogRef.afterClosed().subscribe((result: Partial<RoleDefinition> | undefined) => {
        if (result) {
          // Create a complete RoleDefinition object
          const roleDefinition: RoleDefinition = {
            id: '', // Will be set by the API
            name: result.name || '',
            description: result.description || '',
            criteria: result.criteria || {
              mustExact: [],
              mustFuzzy: [],
              mustNotExact: [],
              mustNotFuzzy: [],
            },
            category: result.category || undefined,
            createdAt: '', // Will be set by the API
            createdBy: '', // Will be set by the API
            scope: 'org', // Default scope
          };

          // Save the new role and select it
          this.roleDefinitionService.create(roleDefinition).subscribe((savedRole) => {
            if (savedRole) {
              this.onSelectionChange(savedRole.id);
              // Also save to recent roles
              this.saveRecentRole(savedRole.id);
            }
          });
        }
      });
    });
  }
}
